"use client";

import type React from "react";
import { useState, useEffect } from "react";
// import { useRouter } from "next/navigation" // Import useParams and useRouter
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  Fab,
} from "@mui/material";
import {
  ArrowBack,
  Business,
  MonetizationOn,
  Email,
  Work,
  School,
  Description,
  Bookmark,
  Share,
  Language,
  CheckCircle,
  Schedule,
  Category,
  Quiz as QuizIcon, // New icon for quiz
  QuestionAnswer as QuestionAnswerIcon, // New icon for questions
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import axios from "axios"; // Assuming axios is used for fetchJobById
 import { IonContent, IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router";
import { fetchJobById } from "../../../api/jobs/jobs";
 
// Define Job type (re-declared for this file's scope)
type Job = {
  job_id?: string;
  title: string;
  job_type: string;
  date_created: string;
  expiration_date: string;
  status?: string;
  category?: string;
  summary?: string;
  responsibilities?: string;
  requirements?: string;
  qualifications?: string;
  benefits?: string;
  min_salary?: string;
  max_salary?: string;
  company_name?: string;
  company_description?: string;
  website?: string;
  application_email?: string;
  description?: string;
  application_link?: string; // Added application_link based on usage
};

// Styled components
const HeroSection = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const InfoCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
  },
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: "1.1rem",
  fontWeight: 600,
  borderRadius: theme.spacing(3),
  textTransform: "none",
  boxShadow: theme.shadows[4],
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[8],
  },
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1000,
}));

interface JobDetailsPageProps {
  jobId: string; // Prop to receive job_id from the parent page.tsx
}

const JobDetails: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const router = useHistory();
  const location = useLocation();
  const history = useHistory();

  // Extract job ID from URL path
  const pathSegments = location.pathname.split("/");
  const jobId = pathSegments[pathSegments.length - 1];

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadJob = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchJobById(jobId);
      setJob(res.data);
    } catch (err) {
      console.error("Error loading job:", err);
      setError("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      loadJob();
    }
  }, [jobId]);

  const formatSalary = (
    min: string | number | undefined,
    max: string | number | undefined
  ) => {
    const minVal = typeof min === "string" ? Number.parseFloat(min) : min || 0;
    const maxVal = typeof max === "string" ? Number.parseFloat(max) : max || 0;

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `${formatter.format(minVal)} - ${formatter.format(maxVal)}`;
  };

  const getTimeUntilExpiration = (expirationDate: string) => {
    const now = new Date();
    const expiry = new Date(expirationDate);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { text: "Expired", color: "error" as const };
    if (diffDays === 0)
      return { text: "Expires today", color: "warning" as const };
    if (diffDays === 1)
      return { text: "Expires tomorrow", color: "warning" as const };
    if (diffDays <= 7)
      return { text: `${diffDays} days left`, color: "warning" as const };
    return { text: `${diffDays} days left`, color: "success" as const };
  };

  const handleApply = () => {
    if (job?.application_link) {
      // alert("You are being redirected to the application link.")
      console.log(
        "Redirecting to application link:",
        `/folder/applications/${job?.application_link}`
      );
      history.push(`/folder/applications/${job?.application_link}`);
    }
    // } else if (job?.website) {
    //   history.push(job.website)
    // } else if (job?.application_email) {
    //   window.open(`mailto:${job.application_email}`)
    // }
  };

  const handleSetQuiz = () => {
    if (jobId) {
      router.push(`/folder/jobs/${jobId}/quiz/set`);
    }
  };

  const handleViewQuizQuestions = () => {
    if (jobId) {
      // router.push(`/jobs/${jobId}/quiz/questions`);
      router.push(`/folder/jobs/${jobId}/quiz`);
    }
  };

  const renderSkeletonLoader = () => (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Paper sx={{ p: 4, mb: 3, borderRadius: 2 }}>
        <Skeleton variant="text" width="60%" height={48} />
        <Skeleton variant="text" width="40%" height={32} sx={{ mt: 2 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Skeleton
            variant="rectangular"
            width={80}
            height={32}
            sx={{ borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            width={100}
            height={32}
            sx={{ borderRadius: 2 }}
          />
        </Stack>
      </Paper>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {[1, 2, 3].map((item) => (
            <Card key={item} sx={{ mb: 3 }}>
              <CardContent>
                <Skeleton variant="text" width="30%" height={32} />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={24}
                  sx={{ mt: 1 }}
                />
                <Skeleton variant="text" width="90%" height={24} />
                <Skeleton variant="text" width="95%" height={24} />
              </CardContent>
            </Card>
          ))}
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="50%" height={32} />
              {[1, 2, 3, 4].map((item) => (
                <Box
                  key={item}
                  sx={{ display: "flex", alignItems: "center", mt: 2 }}
                >
                  <Skeleton
                    variant="circular"
                    width={24}
                    height={24}
                    sx={{ mr: 2 }}
                  />
                  <Skeleton variant="text" width="70%" height={24} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );

  if (loading) {
    return (
      <IonPage className="pushDown">
        <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
          <AppBar position="sticky">
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => router.back()}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ ml: 2 }}>
                Loading...
              </Typography>
            </Toolbar>
          </AppBar>
          {renderSkeletonLoader()}
        </Box>{" "}
      </IonPage>
    );
  }

  // if (error || !job) {
  //   return (
  //     <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
  //       <AppBar position="sticky">
  //         <Toolbar>
  //           <IconButton edge="start" color="inherit" onClick={() => router.back()}>
  //             <ArrowBack />
  //           </IconButton>
  //           <Typography variant="h6" sx={{ ml: 2 }}>
  //             Job Not Found
  //           </Typography>
  //         </Toolbar>
  //       </AppBar>
  //       <Container maxWidth="lg" sx={{ py: 6, textAlign: "center" }}>
  //         <Alert severity="error" sx={{ mb: 3 }}>
  //           {error || "Job not found"}
  //         </Alert>
  //         <Button variant="contained" onClick={() => router.back()}>
  //           Go Back
  //         </Button>
  //       </Container>
  //     </Box>
  //   )
  // }

  const expirationInfo = getTimeUntilExpiration(job?.expiration_date);

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
          {/* Header */}
          <AppBar position="sticky" elevation={2}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => router.back()}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }} noWrap>
                {job?.title}
              </Typography>
              <IconButton color="inherit">
                <Bookmark />
              </IconButton>
              <IconButton color="inherit">
                <Share />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Hero Section */}
            <HeroSection elevation={4}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
                    {job?.title}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 3 }}
                    flexWrap="wrap"
                  >
                    <Chip
                      label={job?.job_type}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={job?.category}
                      variant="outlined"
                      sx={{
                        borderColor: "rgba(255,255,255,0.5)",
                        color: "white",
                      }}
                    />
                    <Chip
                      label={expirationInfo.text}
                      color={expirationInfo.color}
                      sx={{ backgroundColor: "rgba(255,255,255,0.9)" }}
                    />
                  </Stack>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Business sx={{ color: "rgba(255,255,255,0.8)" }} />
                      <Typography variant="h6">{job?.company_name}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MonetizationOn sx={{ color: "rgba(255,255,255,0.8)" }} />
                      <Typography variant="h6">
                        {formatSalary(job?.min_salary, job?.max_salary)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
                {/* <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <ApplyButton
                variant="contained"
                size="large"
                onClick={handleApply}
                sx={{
                  backgroundColor: "white",
                  color: "primary.main",
                  "&:hover": { backgroundColor: "grey.100" },
                }}
              >
                Apply Now
              </ApplyButton>
            </Grid> */}
              </Grid>
            </HeroSection>

            {/* Quiz Management Buttons */}
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* <Button variant="contained" color="primary" startIcon={<QuizIcon />} onClick={handleSetQuiz} size="large">
            Set Quiz for Job
          </Button> */}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<QuestionAnswerIcon />}
                onClick={handleViewQuizQuestions}
                size="large"
              >
                View Quiz Questions
              </Button>
            </Paper>

            <Grid container spacing={3}>
              {/* Main Content */}
              <Grid item xs={12} md={8}>
                {/* Job Summary */}
                <SectionCard elevation={2}>
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Description color="primary" />
                      Job Summary
                    </Typography>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ lineHeight: 1.7 }}
                    >
                      {job?.summary || job?.description}
                    </Typography>
                  </CardContent>
                </SectionCard>
                {/* Company Description */}
                <SectionCard elevation={2}>
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Business color="primary" />
                      About {job?.company_name}
                    </Typography>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ lineHeight: 1.7 }}
                    >
                      {job?.company_description}
                    </Typography>
                  </CardContent>
                </SectionCard>
                {/* Responsibilities */}
                <SectionCard elevation={2}>
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <CheckCircle color="primary" />
                      Responsibilities
                    </Typography>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ lineHeight: 1.7 }}
                    >
                      {job?.responsibilities}
                    </Typography>
                  </CardContent>
                </SectionCard>
                {/* Requirements */}
                <SectionCard elevation={2}>
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <School color="primary" />
                      Requirements
                    </Typography>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ lineHeight: 1.7 }}
                    >
                      {job?.requirements}
                    </Typography>
                  </CardContent>
                </SectionCard>
                {/* Qualifications */}
                <SectionCard elevation={2}>
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Work color="primary" />
                      Qualifications
                    </Typography>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ lineHeight: 1.7 }}
                    >
                      {job?.qualifications}
                    </Typography>
                  </CardContent>
                </SectionCard>
                {/* Benefits */}
                <SectionCard elevation={2}>
                  <CardContent>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <CheckCircle color="primary" />
                      Benefits
                    </Typography>
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{ lineHeight: 1.7 }}
                    >
                      {job?.benefits}
                    </Typography>
                  </CardContent>
                </SectionCard>
              </Grid>
              {/* Sidebar */}
              <Grid item xs={12} md={4}>
                <Card elevation={3} sx={{ position: "sticky", top: 100 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 3 }}
                    >
                      Job Information
                    </Typography>
                    <Stack spacing={3}>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Business color="primary" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Company
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {job?.company_name}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <MonetizationOn color="primary" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Salary Range
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {formatSalary(job?.min_salary, job?.max_salary)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Work color="primary" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Job Type
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {job?.job_type}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Category color="primary" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Category
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {job?.category}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Schedule color="primary" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Application Deadline
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {new Date(
                                job?.expiration_date
                              ).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      {job?.application_email && (
                        <Box>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Email color="primary" />
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Contact Email
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                {job?.application_email}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      )}
                      {job?.website && (
                        <Box>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Language color="primary" />
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Company Website
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  color: "primary.main",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  window.open(job?.website, "_blank")
                                }
                              >
                                {job?.website}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                    <Divider sx={{ my: 3 }} />
                    {/* <ApplyButton variant="contained" fullWidth onClick={handleApply}>
                  Apply for this Job
                </ApplyButton> */}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
          {/* Mobile Apply Button */}
          {isMobile && (
            <StyledFab color="primary" onClick={handleApply}>
              <Work />
            </StyledFab>
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default JobDetails;
